// usePositionMemory — pamat pozicie per route (sek. 53.3)
// Ustava sek. 53.3: po editi -> vratit ma TAM KDE SOM BOL
//   - scroll pozicia
//   - rozbalene accordion / collapsed sekcie
//   - aktivna zalozka / tab
//   - filter a triedenie
//   - vybrany riadok / karta
//
// Implementacia: sessionStorage (per route key), JSON serializacia.
// Per-user dlhodoba pamat (widgety, layouty) bude v DB cez API neskor.
//
// API:
//   const pos = usePositionMemory('zakazka:Z260010')
//   pos.scroll                   — { x, y }
//   pos.saveScroll(x, y)         — ulozi aktualnu scroll poziciu
//   pos.accordions               — { id1: true, id2: false, ... }
//   pos.toggleAccordion(id)
//   pos.tab                      — aktivny tab string
//   pos.setTab(id)
//   pos.filter                   — lubovolny objekt filtra
//   pos.setFilter(obj)
//   pos.selectedRow              — id vybraneho riadku
//   pos.selectRow(id)
//   pos.restoreScroll()          — vrati scroll na ulozenu pozicu (na elemente)
//   pos.clear()                  — vymaze celu pamat pre tento route

import { useState, useEffect, useCallback, useRef } from 'react'

const NS = 'matrix:pos:'   // sessionStorage namespace

function _load(routeKey) {
  if (typeof sessionStorage === 'undefined') return {}
  try {
    const raw = sessionStorage.getItem(NS + routeKey)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

function _save(routeKey, data) {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.setItem(NS + routeKey, JSON.stringify(data))
  } catch (e) {
    // sessionStorage full alebo disabled — silenty fail (pamat pozicie nie je kriticka)
    console.warn('[usePositionMemory] save fail:', e.message)
  }
}

export function usePositionMemory(routeKey) {
  const [state, setState] = useState(() => _load(routeKey))

  // Sync pri zmene routeKey
  const lastKeyRef = useRef(routeKey)
  useEffect(() => {
    if (lastKeyRef.current !== routeKey) {
      setState(_load(routeKey))
      lastKeyRef.current = routeKey
    }
  }, [routeKey])

  // Helper na update state + autosave do sessionStorage
  const update = useCallback((patch) => {
    setState(prev => {
      const next = { ...prev, ...patch }
      _save(routeKey, next)
      return next
    })
  }, [routeKey])

  // Scroll pozicia
  const saveScroll = useCallback((x, y) => {
    update({ scroll: { x: x || 0, y: y || 0 } })
  }, [update])

  // Restoreed scroll — pre konkretny element alebo window
  const restoreScroll = useCallback((element) => {
    const pos = state.scroll
    if (!pos) return
    if (element && element.scrollTo) {
      element.scrollTo(pos.x || 0, pos.y || 0)
    } else if (typeof window !== 'undefined') {
      window.scrollTo(pos.x || 0, pos.y || 0)
    }
  }, [state.scroll])

  // Accordion (expanded/collapsed map)
  const toggleAccordion = useCallback((id) => {
    setState(prev => {
      const accordions = { ...(prev.accordions || {}) }
      accordions[id] = !accordions[id]
      const next = { ...prev, accordions }
      _save(routeKey, next)
      return next
    })
  }, [routeKey])

  const setAccordion = useCallback((id, open) => {
    setState(prev => {
      const accordions = { ...(prev.accordions || {}) }
      accordions[id] = open
      const next = { ...prev, accordions }
      _save(routeKey, next)
      return next
    })
  }, [routeKey])

  // Tab
  const setTab = useCallback((id) => update({ tab: id }), [update])

  // Filter
  const setFilter = useCallback((obj) => update({ filter: obj }), [update])

  // Vybrany riadok
  const selectRow = useCallback((id) => update({ selectedRow: id }), [update])

  // Vymaze celu pamat
  const clear = useCallback(() => {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(NS + routeKey)
    }
    setState({})
  }, [routeKey])

  return {
    scroll: state.scroll || { x: 0, y: 0 },
    saveScroll,
    restoreScroll,
    accordions: state.accordions || {},
    toggleAccordion,
    setAccordion,
    tab: state.tab,
    setTab,
    filter: state.filter,
    setFilter,
    selectedRow: state.selectedRow,
    selectRow,
    clear
  }
}

export default usePositionMemory
